import type { Thunk } from 'editor/src/store/hooks';
import getNewDesignDataForGroup from 'editor/src/store/variants/helpers/getNewDesignDataForGroup';
import { getSizeSpecificAreas } from 'editor/src/store/variants/helpers/sizeSpecificAreas';
import { getDisplayedSize } from 'editor/src/store/variants/selector/getSizeSpecificSizes';
import getVariationGroupsFromDesignOptions from 'editor/src/store/variants/selector/getVariationGroupsFromDesignOptions';
import { setGroupDesignDataAction } from 'editor/src/store/variants/slice';

import getReflectContext from 'editor/src/util/reflectDesignData/getReflectContext';

/**
 * Gives the canvas design to the selected group, and to every group linked to it unless this is a variant switch.
 */
const propagateDesignToGroupsOperation =
  (isVariantSwitch = false): Thunk =>
  (dispatch, getState, { i18n }) => {
    const state = getState();
    const { designData } = state.design;
    if (!state.variants.isVariantFlow || !designData) {
      return;
    }

    const reflectContext = getReflectContext(state);
    const { selectedGroupKey, variationGroups, selectedDesignOptionValue } = state.variants;
    const sourceSize = getSizeSpecificAreas(state.variants.product).length ? getDisplayedSize(state) : undefined;
    const variationGroupsFromDesignOptions = getVariationGroupsFromDesignOptions(state);
    const selectedGroupDesignOption = variationGroupsFromDesignOptions?.find(
      (group) => group.key === selectedDesignOptionValue,
    );
    const selectedGroupKeys = selectedGroupDesignOption?.value.map((group) => group.key);
    const selectedGroup = variationGroups.find((group) => group.key === selectedGroupKey);

    if (!selectedGroup) {
      return;
    }
    if (!selectedGroup.linked || isVariantSwitch) {
      dispatch(
        setGroupDesignDataAction({
          groupKey: selectedGroup.key,
          designData,
          reflectContext,
          sourceSize,
        }),
      );
      return;
    }

    variationGroups.forEach((group) => {
      if (
        (selectedGroupKeys && selectedGroupKeys.find((selectedkey) => selectedkey === group.key) && group.linked) ||
        (!selectedGroupKeys && group.linked)
      ) {
        const groupDesignUpdate = getNewDesignDataForGroup(group, state, i18n);
        dispatch(
          setGroupDesignDataAction({
            groupKey: group.key,
            designData: groupDesignUpdate.designData,
            reflectContext,
            // GAPI-47583 — the group cannot follow this design edit until its
            // template arrives: flag its designData as out of date so the
            // export watcher does not present it to the host as current.
            templatesMissing: groupDesignUpdate.missingTemplate,
            sourceSize,
          }),
        );
      }
    });
  };

export default propagateDesignToGroupsOperation;
